import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpResponse,
    HttpErrorResponse
} from '@angular/common/http';
import { from, Observable, Subscription, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { ErrorDialogService } from '../services/error-dialog.service';
import { LocalStorageService } from '../services/local-storage.service';
import { DataTransferService } from '../services/data-transfer.service';
import { AuthenticationService } from '../services/authentication.service';
import {
    CSRF_HEADER_NAME,
    readCsrfToken,
    requiresCsrfToken
} from '../utils/session-cookie.util';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
    private clearJWTSub: Subscription;
    isAlreadyProcessesedByHttpInterceptor = false;

    constructor(
        public errorDialogService: ErrorDialogService,
        private localstorageService: LocalStorageService,
        private dataTransferService: DataTransferService,
        private authenticationService: AuthenticationService
    ) {
        if (this.clearJWTSub) {
            this.clearJWTSub.unsubscribe();
        }
        // this.clearJWTSub = this.dataTransferService.updateJwtToken$.subscribe(data => this.token = data);
        // this.clearWarehouseSub = this.dataTransferService.updateWarehouseCode$.subscribe(data => this.wareHouseCode = data)
    }

    intercept(
        request: HttpRequest<unknown>,
        next: HttpHandler
    ): Observable<HttpEvent<unknown>> {
        this.isAlreadyProcessesedByHttpInterceptor = true;
        const userLanguage = this.localstorageService.get('userData')
            ?.current_translation
            ? this.localstorageService.get('userData').current_translation
            : 'en';

        let authReq = request;

        if (request.headers.has('X-Bypass-Interceptor')) {
            const headers = request.headers.delete('X-Bypass-Interceptor');
            return next.handle(
                request.clone({
                    headers,
                    withCredentials: true
                })
            );
        }
        /*
         * No Authorization header for the ordinary case any more: the session JWT
         * travels in an HttpOnly cookie that this code cannot read, and
         * withCredentials is what tells the browser to attach it. State changing
         * calls also echo the readable CSRF cookie back in a header, proving the
         * call originated from this app.
         */
        let headers = request.headers.set('X-Language', userLanguage);

        /*
         * Switching into a group retailer is the one exception. It has to act as the
         * parent retailer, whose token the app still holds, while the cookie already
         * belongs to whichever retailer is currently open. The API marks this route
         * as header first so the explicitly attached token wins over the cookie.
         */
        const grpRetailerData = this.localstorageService.get('isGroupRetailer');
        const groupRetailerToken =
            this.localstorageService.get('groupRetailerToken');
        if (grpRetailerData && request.url.includes('login_to_retailer')) {
            headers = headers.set(
                'Authorization',
                'Bearer ' + groupRetailerToken.groupRetailerToken
            );
        }

        const csrfToken = readCsrfToken();
        if (csrfToken && requiresCsrfToken(request.method)) {
            headers = headers.set(CSRF_HEADER_NAME, csrfToken);
        }

        if (this.localstorageService.get('session_uid')) {
            headers = headers.set(
                'Session-UID',
                this.localstorageService.get('session_uid')
            );
        }

        authReq = request.clone({
            headers,
            withCredentials: true
        });

        return next.handle(authReq).pipe(
            map((event: HttpEvent<any>) => {
                if (event instanceof HttpResponse) {
                    return event;
                }
            }),
            // catchError((errorResponse: any) => {
            //     let errMsg: string;
            //     if (errorResponse instanceof HttpErrorResponse) {
            //         const err =
            //             errorResponse.message ||
            //             JSON.stringify(errorResponse.error);
            //         errMsg = `${errorResponse.status} - ${
            //             errorResponse.statusText || ''
            //         } Details: ${err}`;
            //         // this.errorDialogService.openDialog(errorResponse);
            //     } else {
            //         errMsg = errorResponse.message
            //             ? errorResponse.message
            //             : errorResponse.toString();
            //     }
            //     if (
            //         typeof errorResponse?.error?.message === 'string' &&
            //         errorResponse.status !== 403
            //     ) {
            //         this.errorDialogService.openDialog(errorResponse);
            //     }
            //     return throwError(errorResponse);
            // })
            catchError((errorResponse: HttpErrorResponse) => {
                const isSsoConfigurationRequest =
                    errorResponse?.url?.includes('sso/configuration') ||
                    errorResponse?.url?.includes('sso/toggle_sso') ||
                    errorResponse?.url?.includes('tenant_identity_providers');

                const skipErrorMessage = errorResponse.status === 403 ||
                                            (errorResponse.status === 422 && errorResponse?.url?.includes('update_state?action=pack')) ||
                                            (errorResponse.status === 404 && errorResponse?.url?.includes('shipping_lebelsZPL')) ||
                                            (errorResponse.status === 404 && errorResponse?.url?.includes('shipping_lebelsPDF')) ||
                                            (errorResponse.status === 400 && errorResponse?.url?.includes('orders/dispatch_note?order_id')) ||
                                            // Address Book create/update show their own toast via
                                            // getAddressBookApiErrorMessage — skip here to avoid a duplicate.
                                            !!errorResponse?.url?.match(/\/address_books\/(create|\d+\/update)(\?|$)/) ||
                                            errorResponse?.url?.includes('camera_evidence') ||
                                            isSsoConfigurationRequest;
                
                if (skipErrorMessage) {
                    return throwError(() => errorResponse);
                }

                if (errorResponse.error instanceof Blob) {
                    return from(errorResponse.error.text()).pipe(
                        switchMap(text => {
                            let parsedError: any;

                            try {
                                parsedError = JSON.parse(text);
                            } catch {
                                parsedError = { message: text || 'Unknown error' };
                            }

                            const normalizedError = new HttpErrorResponse({
                                error: parsedError,
                                headers: errorResponse.headers,
                                status: errorResponse.status,
                                statusText: errorResponse.statusText,
                                url: errorResponse.url ?? undefined
                            });

                            this.errorDialogService.openDialog(normalizedError);
                            return throwError(() => normalizedError);
                        })
                    );
                }

                this.errorDialogService.openDialog(errorResponse);
                return throwError(() => errorResponse);
            })
        );
    }

    // The ngx-jsonapi package bypasses the HttpClient request pipeline. To handle this package's behavior, we have implemented an XML Middleware.
    // private overrideXMLHttpRequest = (() => {
    //     const originalXhrOpen = XMLHttpRequest.prototype.open;
    //     const originalXhrSend = XMLHttpRequest.prototype.send;
    //     const interceptorInstance = this;
    //     XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    //         const normalizedUrl = interceptorInstance.normalizeUrl(url);
    //         this.url = normalizedUrl;
    //         return originalXhrOpen.apply(this, [
    //             method,
    //             normalizedUrl,
    //             ...rest
    //         ]);
    //     };

    //     XMLHttpRequest.prototype.send = function (body) {
    //         // Use HttpHeaders to get keys and values
    //         if (!interceptorInstance.isAlreadyProcessesedByHttpInterceptor) {
    //             const customHeaders = interceptorInstance.addCustomHeaders(
    //                 new HttpHeaders()
    //             );
    //             customHeaders.keys().forEach((key) => {
    //                 this.setRequestHeader(key, customHeaders.get(key) || '');
    //             });
    //         }
    //         interceptorInstance.isAlreadyProcessesedByHttpInterceptor = false;
    //         return originalXhrSend.apply(this, [body]);
    //     };
    // })();
    // private normalizeUrl(url: string): string {
    //     try {
    //         const urlObj = new URL(url);
    //         const params = new URLSearchParams(urlObj.search);

    //         const updatedParams: string[] = [];

    //         params.forEach((value, key) => {
    //             let normalizedValue = value;

    //             if (this.isDoubleEncoded(value)) {
    //                 normalizedValue = decodeURIComponent(value); // Decode if double-encoded
    //             }

    //             normalizedValue = encodeURIComponent(normalizedValue);

    //             updatedParams.push(`${key}=${normalizedValue}`);
    //         });

    //         const newSearch = updatedParams.join('&');
    //         urlObj.search = newSearch;

    //         return urlObj.toString();
    //     } catch (error) {
    //         return url;
    //     }
    // }
    // private isDoubleEncoded(value: string): boolean {
    //     try {
    //         const firstDecode = decodeURIComponent(value);
    //         return (
    //             firstDecode !== value &&
    //             encodeURIComponent(firstDecode) === value
    //         );
    //     } catch {
    //         return false; // If decoding fails, assume it's not double-encoded
    //     }
    // }

    // private addCustomHeaders(headers: HttpHeaders): HttpHeaders {
    //     this.token = this.localstorageService.get('userToken');
    //     const userLanguage = this.localstorageService.get('userData')
    //         ?.current_translation
    //         ? this.localstorageService.get('userData').current_translation
    //         : 'en';
    //     const token = this.localstorageService.get('jwt');
    //     // Add headers
    //     // Check each header and set only if it doesn't already exist
    //     if (!headers.has('Authorization') && token) {
    //         headers = headers.set('Authorization', `Bearer ${token}`);
    //     }
    //     if (!headers.has('X-Language')) {
    //         headers = headers.set('X-Language', userLanguage);
    //     }
    //     if (!headers.has('Session-UID')) {
    //         headers = headers.set('Session-UID', this.localstorageService.get('session_uid'));
    //     }

    //     return headers;
    // }
}
